import { Loader2, LogIn, Lock, RefreshCw, Trophy } from "lucide-react";
import { useEffect, useState, type MouseEvent } from "react";
import { useTranslation } from "react-i18next";

type Period = "day" | "week" | "month" | "all";
interface LeaderboardEntry { login: string; name: string; avatarUrl: string; total: number; estCostUsd: number; tools: Record<string, number> }
interface MeResponse {
  user: { login: string; name: string; avatarUrl: string };
  total: number;
  reachedTiers: string[];
  nextMilestone: { tier: string; threshold: number } | null;
  devices: Array<{ deviceId: string; lastSeen: string }>;
  unlocks: Array<{ petId: string; name: string; reason: string; season: string | null; posterUrl: string }>;
}

function formatTokens(value: number): string {
  if (value >= 1_000_000_000) return `${(value / 1_000_000_000).toFixed(2)}B`;
  if (value >= 1_000_000) return `${(value / 1_000_000).toFixed(1)}M`;
  if (value >= 1_000) return `${(value / 1_000).toFixed(1)}k`;
  return String(value);
}

function navigateTo(event: MouseEvent<HTMLAnchorElement>, path: string): void {
  event.preventDefault();
  window.history.pushState({}, "", path);
  window.dispatchEvent(new PopStateEvent("popstate"));
}

export function RankPage({ sub }: { sub?: "connect" | "me" }) {
  const [me, setMe] = useState<MeResponse | null>(null);
  const [meLoaded, setMeLoaded] = useState(false);

  useEffect(() => {
    fetch("/api/tokenrank/me")
      .then(async (response) => (response.ok ? (response.json() as Promise<MeResponse>) : null))
      .then(setMe)
      .catch(() => setMe(null))
      .finally(() => setMeLoaded(true));
  }, []);

  if (sub === "connect") return <ConnectPanel me={me} meLoaded={meLoaded} />;
  if (sub === "me") return <MePanel me={me} meLoaded={meLoaded} />;
  return <LeaderboardPanel me={me} />;
}

function LeaderboardPanel({ me }: { me: MeResponse | null }) {
  const { t } = useTranslation();
  const [period, setPeriod] = useState<Period>("all");
  const [entries, setEntries] = useState<LeaderboardEntry[] | null>(null);
  useEffect(() => {
    setEntries(null);
    fetch(`/api/tokenrank/leaderboard?period=${period}`)
      .then(async (response) => (response.ok ? (response.json() as Promise<{ entries: LeaderboardEntry[] }>) : { entries: [] }))
      .then((body) => setEntries(body.entries))
      .catch(() => setEntries([]));
  }, [period]);
  return (
    <section className="rank-page">
      <div className="rank-header">
        <p>{t("rank.privacy")}</p>
        <div className="rank-actions">
          <a className="primary-button" href="/rank/connect" onClick={(event) => navigateTo(event, "/rank/connect")}>
            <Trophy size={16} /> {t("rank.join")}
          </a>
          {me && <a className="secondary-button" href="/rank/me" onClick={(event) => navigateTo(event, "/rank/me")}>{t("rank.myPage")}</a>}
        </div>
      </div>
      <div className="sort-tabs" role="group" aria-label="Leaderboard period">
        {(["day", "week", "month", "all"] as Period[]).map((option) => (
          <button key={option} type="button" className={period === option ? "active" : ""} onClick={() => setPeriod(option)}>{t(`rank.period.${option}`)}</button>
        ))}
      </div>
      {entries === null ? (
        <p className="rank-loading"><Loader2 size={16} className="spin" /> {t("rank.loading")}</p>
      ) : entries.length === 0 ? (
        <p>{t("rank.empty")}</p>
      ) : (
        <ol className="rank-list">
          {entries.map((entry, index) => (
            <li key={entry.login} className="rank-row">
              <span className="rank-position">{index + 1}</span>
              {entry.avatarUrl ? <img className="rank-avatar" src={entry.avatarUrl} alt="" width={32} height={32} /> : <span className="rank-avatar" />}
              <a href={`https://github.com/${entry.login}`} target="_blank" rel="noreferrer">{entry.name || entry.login}</a>
              <span className="rank-tools">{Object.entries(entry.tools).map(([tool, tokens]) => `${tool} ${formatTokens(tokens)}`).join(" · ")}</span>
              <strong>{formatTokens(entry.total)}</strong>
              <span className="rank-cost">≈ ${entry.estCostUsd.toFixed(0)}</span>
            </li>
          ))}
        </ol>
      )}
    </section>
  );
}

function ConnectPanel({ me, meLoaded }: { me: MeResponse | null; meLoaded: boolean }) {
  const { t } = useTranslation();
  const [command, setCommand] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    if (!me) return;
    fetch("/api/tokenrank/connect", { method: "POST" })
      .then(async (response) => (response.ok ? (response.json() as Promise<{ command: string }>) : null))
      .then((body) => setCommand(body?.command ?? null))
      .catch(() => setCommand(null));
  }, [me]);
  if (!meLoaded) return <p className="rank-loading"><Loader2 size={16} className="spin" /> {t("rank.loading")}</p>;
  if (!me) {
    return (
      <section className="rank-page rank-connect">
        <h2>{t("rank.join")}</h2>
        <p>{t("rank.signinHint")}</p>
        <a className="primary-button" href="/api/tokenrank/auth/github"><LogIn size={16} /> {t("rank.signin")}</a>
      </section>
    );
  }
  return (
    <section className="rank-page rank-connect">
      <h2>{t("rank.connectTitle")}</h2>
      <p>{t("rank.connectHint")}</p>
      {command ? (
        <>
          <pre className="rank-command"><code>{command}</code></pre>
          <button className="secondary-button" type="button" onClick={() => { void navigator.clipboard.writeText(command).then(() => setCopied(true)); }}>
            {copied ? t("rank.copied") : t("rank.copy")}
          </button>
        </>
      ) : <p className="rank-loading"><Loader2 size={16} className="spin" /> {t("rank.loading")}</p>}
      <p className="rank-warning"><Lock size={14} /> {t("rank.tokenWarning")}</p>
      <p className="rank-privacy-note">{t("rank.privacy")}</p>
    </section>
  );
}

function MePanel({ me, meLoaded }: { me: MeResponse | null; meLoaded: boolean }) {
  const { t } = useTranslation();
  if (!meLoaded) return <p className="rank-loading"><Loader2 size={16} className="spin" /> {t("rank.loading")}</p>;
  if (!me) {
    return (
      <section className="rank-page">
        <p>{t("rank.signinHint")}</p>
        <a className="primary-button" href="/api/tokenrank/auth/github"><LogIn size={16} /> {t("rank.signin")}</a>
      </section>
    );
  }
  const progress = me.nextMilestone ? Math.min(100, Math.round((me.total / me.nextMilestone.threshold) * 100)) : 100;
  return (
    <section className="rank-page rank-me">
      <div className="rank-me-header">
        {me.user.avatarUrl && <img className="rank-avatar" src={me.user.avatarUrl} alt="" width={48} height={48} />}
        <div>
          <h2>{me.user.name || me.user.login}</h2>
          <p>{formatTokens(me.total)} tokens · {me.devices.length} {t("rank.devices")}</p>
        </div>
      </div>
      <div className="rank-progress">
        <div className="rank-progress-bar" role="progressbar" aria-valuenow={progress} aria-valuemin={0} aria-valuemax={100}>
          <span style={{ width: `${progress}%` }} />
        </div>
        <p>{me.nextMilestone ? t("rank.nextMilestone", { tier: me.nextMilestone.tier, amount: formatTokens(me.nextMilestone.threshold) }) : t("rank.maxMilestone")}</p>
      </div>
      <h3><RefreshCw size={15} /> {t("rank.unlocks")}</h3>
      {me.unlocks.length === 0 ? <p>{t("rank.noUnlocks")}</p> : (
        <div className="rank-unlock-grid">
          {me.unlocks.map((unlock) => (
            <a className="rank-unlock-card" key={unlock.petId} href={`/pets/${unlock.petId}`}>
              <img src={unlock.posterUrl} alt={unlock.name} loading="lazy" />
              <span>{unlock.name}</span>
              <small>{unlock.reason}</small>
            </a>
          ))}
        </div>
      )}
    </section>
  );
}
